import {
  replySubjectFor,
  type CurrentUser,
  type Message,
  type Paginated,
  type ReceiveMode,
  type Room,
  type RoomFilter,
} from "@catmail/shared";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import {
  authApi,
  contactsApi,
  identitiesApi,
  messagesApi,
  roomsApi,
  type RoomCounts,
} from "../api/client";
import { peersOf } from "../lib/roomView";
import { useCurrentIdentity } from "./useCurrentIdentity";
import { useServerEvents } from "./useServerEvents";

const EMPTY_COUNTS: RoomCounts = { all: 0, direct: 0, group: 0, quiet: 0 };

/**
 * 내가 보낸 메시지의 발신자 id. 서버가 참여자 목록에 이 id로 나를 넣어준다
 * (`apps/api/src/api/queries.ts`의 `SELF_ID`). 두 값은 같아야 한다.
 */
const SELF_ID = "me";

type DraftAttachment = {
  filename: string;
  mimeType: string;
  contentBase64: string;
  /** 화면에만 쓰는 값. 서버는 본문에서 다시 잰다 */
  size?: number;
};

/** 보내기 한 번에 넘기는 것. 방에 답하거나(roomId) 새로 시작하거나(to) 둘 중 하나다. */
type SendDraft = {
  roomId?: string;
  to?: string[];
  cc?: string[];
  body: string;
  subject?: string;
  attachments?: DraftAttachment[];
};

/**
 * 받는 방식을 바꿨을 때 띄우는 말.
 *
 * 고른 값(SelfProfilePopover의 RECEIVE_LABEL)과 **같은 낱말**을 쓴다. 고를 때와
 * 바뀐 뒤에 다른 이름이 나오면 같은 설정인지 확신할 수 없다.
 */
const RECEIVE_NOTICE: Record<ReceiveMode, string> = {
  all: "이제 모두 대화 목록으로 와요",
  known: "이제 아는 사람만 대화 목록으로 와요",
  quiet: "이제 모두 조용한 방으로 가요",
};

const FILTERS: readonly RoomFilter[] = ["all", "direct", "group", "quiet"];

/**
 * URL에 얹는 겹쳐 뜨는 화면들.
 *
 * 모달을 컴포넌트 state로만 들고 있으면 **뒤로가기로 닫히지 않는다.** 열어둔 채
 * 새로고침하면 사라지고, 남에게 링크로 줄 수도 없다. 그래서 "지금 무엇이 떠
 * 있는가"도 주소의 일부로 둔다.
 */
const MODAL_VIEWS = [
  "new",
  "profile",
  "files",
  "rename-peer",
  "rename-self",
  "password",
  "avatar",
  "add-address",
  "address",
] as const;

type ModalView = (typeof MODAL_VIEWS)[number];

/**
 * 낙관적 말풍선에 붙일 제목.
 *
 * **정본은 서버(`routes/messages.ts`)다.** 여기서 다시 정하는 이유는 화면이
 * 서버 답을 기다리지 않고 말풍선을 먼저 올리기 때문이고, 그래서 규칙이 어긋나면
 * 그 어긋난 만큼이 그대로 깜빡임이 된다. 세 갈래 모두 저쪽과 같은 순서다.
 */
function optimisticSubjectFor(draft: SendDraft, previous?: Message): string {
  const typed = draft.subject?.trim();
  if (typed) return typed;

  // 앞 메일이 있으면 그 제목을 잇는다. `Re:`가 붙어도 타임라인은 같은 제목으로
  // 본다(normalizeSubject) — 그래도 화면에 뜨는 글자를 서버와 맞춰둔다.
  if (previous) return replySubjectFor(previous.subject);

  // 첫 메일은 서버가 본문 앞부분을 제목으로 삼는다(toSnippet(body, 60)).
  const flat = draft.body.replace(/\s+/g, " ").trim();
  return flat.length <= 60 ? flat : `${flat.slice(0, 59)}…`;
}

function parseFilter(raw: string | null): RoomFilter {
  return FILTERS.includes(raw as RoomFilter) ? (raw as RoomFilter) : "all";
}

function parseView(raw: string | null): ModalView | null {
  return MODAL_VIEWS.includes(raw as ModalView) ? (raw as ModalView) : null;
}

/**
 * 화면 하나가 들고 있어야 하는 상태를 전부 모았다.
 *
 * 서버 상태는 TanStack Query가, **어디를 보고 있는가는 URL이** 들고 있다.
 * 남는 것(스낵바 문구처럼 한 번 쓰고 사라지는 것)만 여기서 state로 든다.
 * 새 메일은 폴링이 아니라 SSE로 들어오고, 이벤트를 받으면 관련 쿼리만 무효화한다.
 */
export function useMailWorkspace() {
  // 열린 방과 원문 화면은 경로가, 목록을 거르는 조건과 떠 있는 모달은
  // 쿼리스트링이 정본이다. 링크 하나로 같은 화면이 다시 열려야 한다.
  const { roomId: routeRoomId = null, messageId: routeMessageId = null } = useParams<{
    roomId: string;
    messageId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filter = parseFilter(searchParams.get("filter"));
  const query = searchParams.get("q") ?? "";
  const view = parseView(searchParams.get("view"));
  // 프로필·파일 창은 목록에서도 열 수 있어서 지금 열린 방과 다를 수 있다.
  const profileRoomId = searchParams.get("room") ?? routeRoomId;
  const profilePeerId = searchParams.get("peer");
  /** 손보는 중인 내 주소의 identity id. 주소창에 얹혀 있어 새로고침해도 남는다 */
  const managedAddressId = searchParams.get("address");

  /** 한 번 쓰고 사라지는 것이라 주소에 남기지 않는다. */
  const [undoNotice, setUndoNotice] = useState<string | null>(null);

  /**
   * 방마다 쓰다 만 글.
   *
   * 입력창이 자기 안에 들고 있으면 방을 옮겨도 같은 인스턴스가 살아남아 **쓰던
   * 글이 따라간다.** 한 사람에게 쓰다 만 문장이 다른 사람 방에 그대로 들어 있고,
   * 채팅처럼 쓰는 앱이라 Enter 한 번이면 그대로 나간다. 메일은 주워담을 수 없다.
   *
   * 주소에 얹지 않는 이유는 남에게 줄 링크에 초안이 딸려가면 안 되기 때문이고,
   * 저장소에 넣지 않는 이유는 초안이 이 창을 넘어 남을 만한 것이 아니어서다.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  /**
   * 내가 push해서 쌓은 모달 기록 수.
   *
   * 모달을 열 때 기록을 하나 쌓으므로 뒤로가기로 닫힌다. 닫기 버튼으로 닫을 때는
   * 그 기록을 **되돌려** 없앤다 — replace로 덮으면 "뒤로 눌렀는데 아무 일도 안
   * 일어나는" 빈 칸이 하나 남는다. 브라우저 뒤로가기로 닫힌 경우엔 여기가 줄지
   * 않지만, 그때는 view가 비면서 아래 effect가 0으로 되돌린다.
   */
  const pushedModals = useRef(0);

  useEffect(() => {
    if (view === null) pushedModals.current = 0;
  }, [view]);

  /** 목록을 거르는 조건만 남긴 쿼리스트링. 방을 옮겨도 이건 따라가야 한다. */
  const listSearch = useCallback(() => {
    const next = new URLSearchParams();
    if (filter !== "all") next.set("filter", filter);
    if (query) next.set("q", query);
    return next;
  }, [filter, query]);

  const patchParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = false) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  // --- 서버 상태 ------------------------------------------------------------

  const meQuery = useQuery({ queryKey: ["me"], queryFn: authApi.me, retry: false });

  /**
   * 지금 쓰는 주소 = 지금 계정.
   *
   * 아래 조회가 전부 이 값에 매달린다. 쿼리 키에도 넣어야 갈아탄 순간 앞 계정의
   * 목록이 남지 않는다 — 키가 같으면 react-query가 캐시된 남의 방을 그대로 보여준다.
   */
  const { current: identity, options: identityOptions, select: selectIdentity } =
    useCurrentIdentity(meQuery.data?.id ?? "", meQuery.data?.addresses ?? []);
  const identityId = identity?.id ?? null;

  const roomsQuery = useInfiniteQuery({
    queryKey: ["rooms", identityId, filter, query],
    queryFn: ({ pageParam }) =>
      roomsApi.list({ filter, query, cursor: pageParam, identityId }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: meQuery.isSuccess,
    // 탭을 옮기거나 검색어를 한 글자 칠 때마다 목록이 빈 칸으로 깜빡이지 않게,
    // 새 결과가 올 때까지 앞의 것을 그대로 둔다.
    placeholderData: keepPreviousData,
  });

  const countsQuery = useQuery({
    queryKey: ["counts", identityId],
    queryFn: () => roomsApi.counts(identityId),
    enabled: meQuery.isSuccess,
  });

  const messagesQuery = useInfiniteQuery({
    queryKey: ["messages", routeRoomId],
    queryFn: ({ pageParam }) => roomsApi.messages(routeRoomId ?? "", pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: meQuery.isSuccess && routeRoomId !== null,
  });

  /**
   * 프로필·파일 창이 가리키는 방의 메시지.
   *
   * 목록에서 아바타를 눌러 프로필을 열면 **지금 열려 있는 대화와 다른 방**일 수
   * 있다. 그때 화면에 이미 있는 `messages`를 쓰면 엉뚱한 방의 파일 개수가 뜨고,
   * "주고받은 파일"에도 남의 방 첨부가 늘어선다.
   *
   * 열린 방과 같으면 쿼리 키가 같아 위 조회의 결과를 그대로 나눠 쓴다.
   */
  const profileMessagesQuery = useInfiniteQuery({
    queryKey: ["messages", profileRoomId],
    queryFn: ({ pageParam }) => roomsApi.messages(profileRoomId ?? "", pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: meQuery.isSuccess && profileRoomId !== null && view !== null,
  });

  // 새 메일이 오면 목록·개수·열린 방만 다시 읽는다.
  useServerEvents(meQuery.isSuccess, (event) => {
    switch (event.type) {
      case "message.new":
        void queryClient.invalidateQueries({ queryKey: ["messages", event.roomId] });
        void queryClient.invalidateQueries({ queryKey: ["rooms"] });
        void queryClient.invalidateQueries({ queryKey: ["counts"] });
        break;
      case "room.updated":
      case "room.read":
        void queryClient.invalidateQueries({ queryKey: ["rooms"] });
        void queryClient.invalidateQueries({ queryKey: ["counts"] });
        break;
      case "message.delivery":
        void queryClient.invalidateQueries({ queryKey: ["messages"] });
        break;
      case "presence":
        // 접속 상태는 참여자에 실려 오므로 방을 다시 읽으면 점이 따라온다.
        // 안 읽은 개수는 안 바뀌었으니 그건 건드리지 않는다.
        void queryClient.invalidateQueries({ queryKey: ["rooms"] });
        void queryClient.invalidateQueries({ queryKey: ["contacts"] });
        break;
    }
  });

  const rooms = useMemo(
    () => roomsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [roomsQuery.data],
  );

  const messages = useMemo(() => {
    // 각 페이지 안은 시간순이지만, 뒤 페이지일수록 더 과거다(위로 올라가며 읽으므로).
    // 그대로 이으면 순서가 뒤집히므로 페이지 배열을 먼저 뒤집는다.
    const pages = messagesQuery.data?.pages ?? [];
    return [...pages].reverse().flatMap((page) => page.items);
  }, [messagesQuery.data]);

  const profileMessages = useMemo(() => {
    const pages = profileMessagesQuery.data?.pages ?? [];
    return [...pages].reverse().flatMap((page) => page.items);
  }, [profileMessagesQuery.data]);

  const listedRoom = useMemo(
    () => rooms.find((room) => room.id === routeRoomId) ?? null,
    [rooms, routeRoomId],
  );

  /**
   * 목록에 없는 방을 링크로 열었을 때 직접 물어본다.
   *
   * 목록은 50개씩 잘려 오고 필터로도 걸러진다. "1:1 대화"를 보는 중에 단체방
   * 링크를 열거나 오래된 방을 열면 목록에는 없다. 그때 목록만 믿으면 멀쩡한 방이
   * 빈 화면이 된다.
   */
  const roomFallbackQuery = useQuery({
    queryKey: ["room", routeRoomId],
    queryFn: () => roomsApi.get(routeRoomId ?? ""),
    enabled: meQuery.isSuccess && routeRoomId !== null && listedRoom === null,
    retry: false,
  });

  const selectedRoom = listedRoom ?? roomFallbackQuery.data ?? null;

  /**
   * 열린 방이 다른 계정 것이면 **그 계정으로 갈아탄다.**
   *
   * 링크를 받아 열거나, 방을 열어둔 채 브라우저를 껐다 켠 경우다. 그대로 두면
   * `netflix@`의 대화를 보면서 왼쪽 목록에는 `yena@`의 방만 있고, 거기서 뭘
   * 눌러도 보던 대화가 목록에 없다. 방은 주소에 속하므로(architecture.md §1)
   * 보고 있는 방이 곧 지금 계정이어야 앞뒤가 맞는다.
   */
  useEffect(() => {
    const owner = selectedRoom?.identityId;
    // 옛 API는 방에 주소를 안 실어 보낸다. 모르면 갈아타지 않는다.
    if (!owner || !identityId || owner === identityId) return;
    if (!identityOptions.some((entry) => entry.id === owner)) return;
    selectIdentity(owner);
  }, [selectedRoom, identityId, identityOptions, selectIdentity]);

  /**
   * 링크는 받았는데 내 방이 아니거나 지워진 경우. 빈 화면 대신 그렇게 말해준다.
   *
   * `listedRoom === null`을 같이 보는 것은 위 조회의 enable 조건과 맞추기 위해서다.
   * 목록보다 fallback이 먼저 도착해 한 번 실패하면(`retry: false`) 그 에러는 캐시에
   * 남고, 뒤늦게 목록이 와서 방이 멀쩡히 열려도 지워지지 않는다 — 없는 방이라고
   * 거짓말을 하게 된다.
   */
  const roomNotFound =
    routeRoomId !== null && listedRoom === null && roomFallbackQuery.isError;

  const profileRoom = useMemo(() => {
    if (profileRoomId === null) return null;
    if (profileRoomId === selectedRoom?.id) return selectedRoom;
    return rooms.find((room) => room.id === profileRoomId) ?? null;
  }, [rooms, profileRoomId, selectedRoom]);

  const htmlMessage = useMemo(
    () => messages.find((message) => message.id === routeMessageId) ?? null,
    [messages, routeMessageId],
  );

  /**
   * 원문 주소를 열었는데 그 메일이 지금 불러온 구간에 없다.
   *
   * 방을 열면 최근 60통을 가져오므로 방금 누른 원문은 언제나 그 안에 있다.
   * 한참 위로 올라가 본 옛날 메일의 원문 링크를 나중에 다시 여는 경우만 여기
   * 걸리는데, 그때는 대화로 돌려보낸다 — 빈 화면보다는 방이 낫다.
   */
  const htmlMissing =
    routeMessageId !== null && !messagesQuery.isPending && htmlMessage === null;

  /** 프로필에 띄울 "이 사람과 같이 있는 다른 방들" */
  const sharedRooms = useMemo(() => {
    if (!profileRoom) return [];

    const peer = peersOf(profileRoom)[0];
    if (!peer) return [];

    return rooms.filter(
      (room) =>
        room.id !== profileRoom.id &&
        room.participants.some((participant) => participant.address === peer.address),
    );
  }, [rooms, profileRoom]);

  const user: CurrentUser = useMemo(
    () => ({
      id: meQuery.data?.id ?? "",
      displayName: meQuery.data?.displayName ?? "",
      initials: (meQuery.data?.displayName ?? "?").slice(0, 1),
      colorIndex: 0,
      avatarUrl: meQuery.data?.avatarUrl ?? null,
      primaryAddress: meQuery.data?.primaryAddress ?? "",
      addresses: meQuery.data?.addresses ?? [],
      receiveMode: meQuery.data?.receiveMode ?? "all",
      // 못 읽었으면 집사 없음으로. 모른다고 위 등급 기능을 열어주면 안 된다.
      plan: meQuery.data?.plan ?? "none",
      // 없으면 없는 대로 둔다. 지어내면 아래 needsApproval이 거짓으로 켜진다.
      ...(meQuery.data?.status ? { status: meQuery.data.status } : {}),
    }),
    [meQuery.data],
  );

  // --- 어디를 보고 있는가 ----------------------------------------------------

  const setFilter = useCallback(
    (next: RoomFilter) => {
      // 탭은 네 개뿐이라 기록에 남겨도 되고, 남겨야 뒤로가기로 되돌아온다.
      // 방을 열어둔 채 탭을 바꾸면 목록만 갈아끼운다 — 보던 대화를 닫지 않는다.
      const params = new URLSearchParams();
      if (next !== "all") params.set("filter", next);
      if (query) params.set("q", query);
      setSearchParams(params);
    },
    [query, setSearchParams],
  );

  const setQuery = useCallback(
    (next: string) => {
      // 검색은 한 글자마다 바뀌므로 기록에 쌓지 않는다. 쌓으면 뒤로가기 한 번에
      // 한 글자씩 지워진다.
      patchParams((params) => {
        if (next) params.set("q", next);
        else params.delete("q");
        // 검색을 시작하면 떠 있던 창은 접는다.
        params.delete("view");
      }, true);
    },
    [patchParams],
  );

  const selectRoom = useCallback(
    (roomId: string) => {
      void navigate({
        pathname: `/r/${roomId}`,
        search: listSearch().toString(),
      });
    },
    [navigate, listSearch],
  );

  /**
   * 프로필 창에서 그 방으로 건너간다.
   *
   * 여기서 `selectRoom`을 그대로 쓰면 **이미 그 방을 보고 있을 때 아무 일도
   * 일어나지 않는다.** 주소가 그대로라 라우터가 움직이지 않고, 프로필 창은 열린
   * 채 남는다 — 누른 사람 눈에는 "채팅하기"가 고장난 것으로 보인다. 창을 닫는
   * 것까지가 이 동작이다.
   */
  const openRoomFromProfile = useCallback(
    (roomId: string) => {
      // 창을 열며 쌓아둔 기록이 있으면 그 자리를 방 주소로 갈아끼운다. 그래야
      // 뒤로가기가 방금 닫은 프로필로 되돌아가지 않는다.
      const replace = pushedModals.current > 0;
      pushedModals.current = 0;

      void navigate(
        { pathname: `/r/${roomId}`, search: listSearch().toString() },
        { replace },
      );
    },
    [navigate, listSearch],
  );

  /**
   * 열어둔 방을 닫고 목록으로 돌아간다. 거르는 조건은 들고 간다.
   *
   * `setFilter`로는 여기서 못 빠져나온다 — 쿼리스트링만 갈아끼우므로 경로에 박힌
   * `/r/<없는 방>`이 그대로 남아 "찾을 수 없어요" 화면이 다시 그려진다.
   */
  const goHome = useCallback(() => {
    void navigate({ pathname: "/", search: listSearch().toString() });
  }, [navigate, listSearch]);

  const openView = useCallback(
    (next: ModalView, extra?: Record<string, string>) => {
      pushedModals.current += 1;
      patchParams((params) => {
        params.set("view", next);
        params.delete("room");
        params.delete("peer");
        params.delete("address");
        for (const [key, value] of Object.entries(extra ?? {})) {
          params.set(key, value);
        }
      });
    },
    [patchParams],
  );

  const closeView = useCallback(() => {
    if (pushedModals.current > 0) {
      pushedModals.current -= 1;
      void navigate(-1);
      return;
    }

    // 링크로 바로 열린 경우엔 되돌아갈 자리가 없으니 주소만 지운다.
    patchParams((params) => {
      params.delete("view");
      params.delete("room");
      params.delete("peer");
      params.delete("address");
    }, true);
  }, [navigate, patchParams]);

  /**
   * 원문 화면을 내가 열어서 기록이 하나 쌓였는가.
   *
   * 링크를 받아 원문으로 **바로** 들어온 경우엔 돌아갈 앞자리가 없다. 그때
   * `navigate(-1)`을 하면 대화로 가는 게 아니라 앱 밖으로 나가버린다.
   */
  const pushedHtml = useRef(false);

  useEffect(() => {
    if (routeMessageId === null) pushedHtml.current = false;
  }, [routeMessageId]);

  const openHtml = useCallback(
    (message: Message) => {
      pushedHtml.current = true;
      void navigate({
        pathname: `/r/${message.roomId}/m/${message.id}`,
        search: listSearch().toString(),
      });
    },
    [navigate, listSearch],
  );

  const closeHtml = useCallback(() => {
    if (pushedHtml.current) {
      pushedHtml.current = false;
      void navigate(-1);
      return;
    }

    void navigate(
      { pathname: `/r/${routeRoomId ?? ""}`, search: listSearch().toString() },
      { replace: true },
    );
  }, [navigate, listSearch, routeRoomId]);

  // --- 동작 -----------------------------------------------------------------

  const readMutation = useMutation({
    mutationFn: roomsApi.markRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      void queryClient.invalidateQueries({ queryKey: ["counts"] });
    },
  });

  /**
   * 열어둔 방은 읽음으로 표시한다.
   *
   * 예전에는 목록에서 **누를 때만** 표시했다. 이제 열린 방이 주소에서 오므로
   * 링크로 들어오거나 새로고침한 경우에도 똑같이 읽은 것으로 쳐야 한다.
   *
   * ⚠️ **`unreadCount`도 같이 본다.** 예전에는 방 id만 봐서, 보고 있는 방에
   * 메일이 새로 오면 읽음 처리가 안 됐다 — 서버 이벤트가 목록을 다시 불러
   * 안 읽음이 1이 되는데 방 id는 그대로라 이 효과가 다시 돌지 않았다. 화면에는
   * 그 말풍선이 뻔히 떠 있는데 목록에는 빨간 1이 남고, 방을 나갔다 들어와야
   * 없어졌다.
   *
   * 되풀이될 걱정은 없다. 읽음 처리가 끝나면 `unreadCount`가 0이 되므로 조건이
   * 스스로 닫힌다.
   */
  const markRead = readMutation.mutate;
  // 방 객체가 아니라 값 둘만 본다. 목록을 다시 부를 때마다 객체는 새로 만들어져서,
  // 그걸 그대로 넣으면 읽을 것이 없어도 효과가 계속 돈다.
  const openRoomId = selectedRoom?.id ?? null;
  const openRoomUnread = selectedRoom?.unreadCount ?? 0;
  useEffect(() => {
    if (openRoomId && openRoomUnread > 0) markRead(openRoomId);
  }, [openRoomId, openRoomUnread, markRead]);

  // 방을 옮기면 스낵바를 치운다. 다른 화면으로 간 뒤에도 남아 있으면 어색하다.
  useEffect(() => {
    setUndoNotice(null);
  }, [routeRoomId]);

  const sendMutation = useMutation({
    mutationFn: (draft: SendDraft) =>
      // `size`는 낙관적 말풍선에 파일 크기를 적으려고 들고만 있는 값이라
      // 서버로는 보내지 않는다.
      messagesApi.send({
        ...draft,
        ...(draft.attachments
          ? {
              attachments: draft.attachments.map(
                ({ filename, mimeType, contentBase64 }) => ({
                  filename,
                  mimeType,
                  contentBase64,
                }),
              ),
            }
          : {}),
      }),

    /**
     * 누른 즉시 화면에 올린다.
     *
     * 예전에는 POST가 돌아오기를 기다렸다가 다시 목록을 받아왔다 — **왕복 두 번**
     * 뒤에야 자기가 쓴 말이 보였다. 메일이라 실제 발송은 어차피 큐가 15초마다
     * 가져가므로, 서버가 "받았다"고 답하기를 기다릴 이유가 없다. 답장을 쓰는
     * 감각에서 이 한 순간이 제일 중요하다.
     *
     * 새 대화(roomId 없음)는 어느 방에 넣을지 모르므로 그냥 보낸다.
     */
    onMutate: async (draft: SendDraft) => {
      if (!draft.roomId) return undefined;

      const key = ["messages", draft.roomId];
      // 진행 중인 조회가 뒤늦게 도착해 방금 끼워 넣은 것을 덮지 않게 막는다.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<InfiniteData<Paginated<Message>>>(key);

      queryClient.setQueryData<InfiniteData<Paginated<Message>>>(key, (old) => {
        if (!old || old.pages.length === 0) return old;

        const optimistic: Message = {
          id: `pending-${crypto.randomUUID()}`,
          roomId: draft.roomId ?? "",
          direction: "outgoing",
          senderId: SELF_ID,
          /*
           * 제목도 **서버와 같은 규칙으로** 미리 정한다.
           *
           * 예전에는 빈 문자열로 뒀는데, 타임라인은 제목이 바뀐 자리에 줄을
           * 넣으므로(lib/timeline.ts) 빈 제목이 곧 "다른 이야기"가 됐다. 보내는
           * 순간 구분선이 하나 생겼다가 서버 답이 오면 사라져서, 누를 때마다
           * 대화가 한 번 덜컥이고 없던 줄이 번쩍였다.
           */
          subject: optimisticSubjectFor(draft, old.pages[0]?.items.at(-1)),
          body: draft.body,
          hasHtml: false,
          attachments: (draft.attachments ?? []).map((file, index) => ({
            id: `pending-${index}`,
            filename: file.filename,
            mimeType: file.mimeType,
            size: file.size ?? 0,
            inline: false,
            contentId: null,
            offloaded: false,
            // 아직 서버에 없으니 내려받을 곳도 없다. 눌러도 아무 일 없다.
            downloadUrl: "",
          })),
          sentAt: new Date().toISOString(),
          readAt: null,
          readBy: 0,
          // 실제로 큐에 들어간 상태와 같은 값이다. 말풍선에 시각 대신 시계가 뜬다.
          delivery: "queued",
          error: null,
        };

        // 0번 페이지가 가장 최근이고 그 안은 시간순이다. 맨 뒤가 대화의 끝.
        const [newest, ...rest] = old.pages;
        return {
          ...old,
          pages: [{ ...newest!, items: [...newest!.items, optimistic] }, ...rest],
        };
      });

      return { key, previous };
    },

    onError: (_error, _draft, context) => {
      // 못 보냈으면 없던 일로 되돌린다. 남겨두면 보낸 줄 알고 넘어간다.
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      setUndoNotice("보내지 못했어요. 잠시 뒤 다시 시도해 주세요");
    },

    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["messages", result.roomId] });
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
    },
  });

  const sendMessage = useCallback(
    (body: string, attachments: DraftAttachment[] = [], subject?: string) => {
      if (!selectedRoom) return;

      sendMutation.mutate({
        roomId: selectedRoom.id,
        body,
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(subject ? { subject } : {}),
      });

      // 보냈으니 이 방의 초안은 없앤다. 남겨두면 다시 열 때 방금 보낸 말이
      // 입력창에 또 들어 있어서 두 번 보낸 줄 안다.
      setDrafts((current) => {
        const { [selectedRoom.id]: _sent, ...rest } = current;
        return rest;
      });
    },
    [selectedRoom, sendMutation],
  );

  /**
   * 못 보낸 메일 다시 보내기.
   *
   * 실패한 말풍선에 사유만 적혀 있고 손쓸 방법이 없으면, 사용자는 그 말을
   * 지우지도 보내지도 못한 채 대화 안에 남겨두게 된다.
   */
  const retryMutation = useMutation({
    mutationFn: messagesApi.retry,
    onSuccess: (result) => {
      if (result.message) {
        void queryClient.invalidateQueries({
          queryKey: ["messages", result.message.roomId],
        });
      }
      setUndoNotice("다시 보내는 중이에요");
    },
    onError: () => setUndoNotice("다시 보내지 못했어요"),
  });

  const quietMutation = useMutation({
    mutationFn: (input: { roomId: string; quiet: boolean }) =>
      roomsApi.setQuiet(input.roomId, input.quiet),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      void queryClient.invalidateQueries({ queryKey: ["counts"] });
    },
  });

  const toggleQuiet = useCallback(
    (roomId: string) => {
      const room =
        rooms.find((candidate) => candidate.id === roomId) ??
        (selectedRoom?.id === roomId ? selectedRoom : null);
      if (!room) return;

      quietMutation.mutate({ roomId, quiet: !room.isQuiet });
      closeView();
      // "중요한 채팅방"은 쓰지 않는다. 이 앱은 중요도를 다루지 않고, 조용한
      // 방의 반대는 그냥 목록에 뜨는 자리다. 화면에 보이는 이름으로 부른다.
      setUndoNotice(
        room.isQuiet ? "대화 목록으로 옮겼어요" : "조용한 방으로 옮겼어요",
      );
    },
    [rooms, selectedRoom, quietMutation, closeView],
  );

  const readAllMutation = useMutation({
    mutationFn: roomsApi.readAllQuiet,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      void queryClient.invalidateQueries({ queryKey: ["counts"] });
    },
  });

  /**
   * 새 대화를 시작한다 = **메일 한 통을 보낸다.**
   *
   * 예전에는 상대를 고르는 순간 "안녕하세요"를 발송했다. 채팅 앱 감각으로는
   * 방만 열리는 게 당연한데 여기서는 진짜 메일이 나가서, 상대 메일함에 인사만
   * 덜렁 든 메일이 남았다. 이제 무엇을 보낼지 쓴 다음에만 나간다.
   */
  /**
   * 새 채팅 — **방만 연다. 메일은 나가지 않는다.**
   *
   * 예전에는 이 자리에서 첫 메일을 보냈다. 방이 메일이 오갈 때만 생기는 구조라
   * 그럴 수밖에 없었고, 그래서 말을 걸려면 먼저 무슨 말을 할지 정해야 했다.
   * 이제 서버가 빈 방을 열어주므로 사람만 고르면 된다.
   */
  const roomCreateMutation = useMutation({
    mutationFn: (input: { to: string[] }) =>
      roomsApi.create({
        // 새 대화는 **지금 고른 계정에서** 열린다. 예전에는 늘 대표 주소였다 —
        // 업무 주소를 골라두고 새 대화를 시작해도 개인 주소로 나갔고, 답장은 그
        // 주소로 돌아와 방까지 다른 곳에 생겼다.
        to: input.to,
        identityId,
      }),
    onSuccess: (room, input) => {
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });

      // 방금 연 방이 보이는 탭으로 옮겨가며 연다. 모달을 닫는 기록까지 겹쳐
      // 쌓지 않도록 열기 전 자리를 이 주소로 갈아끼운다.
      pushedModals.current = 0;
      const params = new URLSearchParams();
      params.set("filter", input.to.length > 1 ? "group" : "direct");
      void navigate(
        { pathname: `/r/${room.id}`, search: params.toString() },
        { replace: true },
      );
    },
  });

  const startChat = useCallback(
    (draft: { to: string[] }) => roomCreateMutation.mutate({ to: draft.to }),
    [roomCreateMutation],
  );

  // --- 프로필 --------------------------------------------------------------

  const profileMutation = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const changeReceiveMode = useCallback(
    (mode: ReceiveMode) => {
      profileMutation.mutate(
        { receiveMode: mode },
        { onSuccess: () => setUndoNotice(RECEIVE_NOTICE[mode]) },
      );
    },
    [profileMutation],
  );

  const renameSelf = useCallback(
    (displayName: string) => {
      profileMutation.mutate(
        { displayName },
        {
          onSuccess: () => {
            closeView();
            setUndoNotice("이름을 바꿨어요");
          },
        },
      );
    },
    [profileMutation, closeView],
  );

  /**
   * 프로필 사진.
   *
   * 올리고 나면 `me`만 다시 읽으면 될 것 같지만 **방 목록도 같이 읽는다** —
   * 같은 서버 사람에게는 내 사진이 보이므로(참여자 DTO에 실린다) 내 화면의
   * 상대 아바타가 아니라 *상대 화면의 내 아바타*가 바뀌는데, 내 화면에도 내가
   * 참여자로 들어 있는 방이 있다.
   */
  const avatarMutation = useMutation({
    mutationFn: authApi.uploadAvatar,
    onSuccess: () => {
      closeView();
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      setUndoNotice("프로필 사진을 바꿨어요");
    },
  });

  const removeAvatarMutation = useMutation({
    mutationFn: authApi.removeAvatar,
    onSuccess: () => {
      closeView();
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      setUndoNotice("프로필 사진을 지웠어요");
    },
  });

  const passwordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: (result) => {
      closeView();
      setUndoNotice(
        result.signedOutSessions > 0
          ? `비밀번호를 바꿨어요. 다른 기기 ${result.signedOutSessions}곳의 로그인을 끊었어요`
          : "비밀번호를 바꿨어요",
      );
    },
  });

  /**
   * 주소 신청.
   *
   * 성공해도 그 주소로 메일이 오지는 않는다. 그래서 "만들었어요"가 아니라
   * **기다리는 중**이라고 말한다 — 열린 줄 알고 어디에 적어두면 그 메일은
   * 엣지에서 거절당해 사라진다.
   */
  const addressMutation = useMutation({
    mutationFn: identitiesApi.request,
    onSuccess: (result) => {
      closeView();
      // 목록에 기다리는 주소가 바로 뜨게 한다. 안 그러면 신청이 먹혔는지 모른다.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setUndoNotice(`${result.address} 을 신청했어요. 받아들여지면 바로 쓸 수 있어요`);
    },
  });

  /**
   * 주소 껐다 켜기.
   *
   * 지우는 것과 다르다는 점을 말투로도 지킨다 — 껐다고 사라진 것이 아니다.
   */
  const addressEnableMutation = useMutation({
    mutationFn: (input: { identityId: string; enabled: boolean; address: string }) =>
      identitiesApi.setEnabled(input.identityId, input.enabled),
    onSuccess: (_result, input) => {
      closeView();
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setUndoNotice(
        input.enabled
          ? `${input.address} 을 다시 켰어요`
          : `${input.address} 을 껐어요. 다시 켜면 그대로 돌아와요`,
      );
    },
  });

  /**
   * 주소 지우기.
   *
   * 그 주소로 온 **대화까지 함께 사라진다**(연쇄 삭제). 그래서 방 목록과 개수도
   * 다시 부른다 — 안 그러면 지워진 방이 목록에 남아 눌러도 안 열린다.
   */
  const addressRemoveMutation = useMutation({
    mutationFn: (input: { identityId: string; address: string }) =>
      identitiesApi.remove(input.identityId),
    onSuccess: (_result, input) => {
      closeView();
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      setUndoNotice(`${input.address} 을 지웠어요`);
    },
  });

  // --- 상대 ----------------------------------------------------------------

  const contactMutation = useMutation({
    mutationFn: (input: {
      contactId: string;
      changes: { customName?: string; blocked?: boolean };
    }) => contactsApi.update(input.contactId, input.changes),
    onSuccess: () => {
      // 별칭은 방 제목과 미리보기에도 나오므로 목록까지 다시 부른다.
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  /** 프로필 창에 떠 있는 상대. 1:1 방에서만 의미가 있다. */
  const profilePeer = useMemo(() => {
    if (!profileRoom) return null;
    const peers = peersOf(profileRoom);
    return peers.find((peer) => peer.id === profilePeerId) ?? peers[0] ?? null;
  }, [profileRoom, profilePeerId]);

  const renamePeer = useCallback(
    (customName: string) => {
      if (!profilePeer) return;
      contactMutation.mutate(
        { contactId: profilePeer.id, changes: { customName } },
        {
          onSuccess: () => {
            closeView();
            setUndoNotice(
              customName.length > 0 ? "이름을 바꿨어요" : "원래 이름으로 되돌렸어요",
            );
          },
        },
      );
    },
    [contactMutation, profilePeer, closeView],
  );

  const toggleBlockPeer = useCallback(() => {
    if (!profilePeer) return;
    const blocked = !profilePeer.blocked;

    contactMutation.mutate(
      { contactId: profilePeer.id, changes: { blocked } },
      {
        onSuccess: () => {
          closeView();
          setUndoNotice(
            blocked
              ? "차단했어요. 앞으로 오는 메일은 저장하지 않아요"
              : "차단을 풀었어요",
          );
        },
      },
    );
  }, [contactMutation, profilePeer, closeView]);

  const signOut = useCallback(() => {
    void authApi.logout().then(() => {
      queryClient.clear();
      void navigate("/login", { replace: true });
    });
  }, [navigate, queryClient]);

  return {
    user,
    rooms,
    counts: countsQuery.data ?? EMPTY_COUNTS,
    filter,
    query,
    selectedRoom,
    roomNotFound,
    /**
     * 주소가 가리키는 방 id. 아직 못 불러왔어도 값이 있다.
     *
     * 좁은 화면에서 목록과 대화 중 무엇을 보일지 가르는 기준이라, **불러오는
     * 동안에도** 정해져 있어야 한다. `selectedRoom`으로 가르면 방이 도착하기
     * 전까지 목록이 잠깐 보였다가 대화로 바뀐다.
     */
    routeRoomId,
    messages,
    profileRoom,
    /** 프로필·파일 창이 가리키는 방의 메시지. 열린 대화의 것과 다를 수 있다 */
    profileMessages,
    sharedRooms,
    htmlMessage,
    htmlMissing,
    undoNotice,

    // 첫 화면인지, 잠깐 비어 있는 것인지 구분해야 "아직 대화가 없어요"를
    // 엉뚱한 때에 띄우지 않는다.
    isLoadingRooms: meQuery.isLoading || roomsQuery.isPending,
    isLoadingMessages: routeRoomId !== null && messagesQuery.isPending,
    isUnauthenticated: meQuery.isError,
    /**
     * 로그인은 됐는데 아직 관리자가 받아주지 않았다.
     *
     * **서버가 말했을 때만** 판단한다. 옛 API는 `status`를 안 보내는데, 없는 것을
     * 대기로 치면 멀쩡한 사람이 전부 승인 화면에 갇힌다. 진짜 문지기는
     * 서버(`requireApproved`)이므로 화면이 앞질러 잠글 이유가 없다.
     */
    needsApproval:
      meQuery.isSuccess &&
      meQuery.data.status !== undefined &&
      meQuery.data.status !== "approved",
    isAdmin: meQuery.data?.isAdmin === true,
    /** 로그인은 됐는데 서버를 못 부르는 상태 */
    loadFailed: roomsQuery.isError,
    retryLoad: () => {
      void roomsQuery.refetch();
      void countsQuery.refetch();
    },

    // 위로 스크롤하면 더 과거를, 방 목록은 아래로 내리면 더 오래된 방을 부른다.
    hasOlderMessages: messagesQuery.hasNextPage,
    isLoadingOlder: messagesQuery.isFetchingNextPage,
    loadOlderMessages: () => {
      if (messagesQuery.hasNextPage && !messagesQuery.isFetchingNextPage) {
        void messagesQuery.fetchNextPage();
      }
    },
    hasMoreRooms: roomsQuery.hasNextPage,
    loadMoreRooms: () => {
      if (roomsQuery.hasNextPage && !roomsQuery.isFetchingNextPage) {
        void roomsQuery.fetchNextPage();
      }
    },

    setFilter,
    setQuery,
    selectRoom,
    openRoomFromProfile,
    goHome,

    // --- 겹쳐 뜨는 화면 -----------------------------------------------------
    newChatOpen: view === "new",
    openNewChat: () => openView("new"),
    // 이름을 고치는 동안에도 프로필은 뒤에 떠 있어야 한다. 사라졌다가 돌아오면
    // 뒤로가기로 닫았을 때 화면이 두 번 바뀐다.
    profileOpen: view === "profile" || view === "rename-peer",
    openProfile: (roomId: string, peerId?: string) =>
      openView("profile", { room: roomId, ...(peerId ? { peer: peerId } : {}) }),
    filesOpen: view === "files",
    // 프로필에서 열면 **그 프로필의 방**, 대화창에서 열면 열려 있는 방이다.
    // selectedRoom으로 고정하면 목록에서 연 프로필의 "주고받은 파일"이
    // 엉뚱하게 지금 보고 있는 대화의 첨부를 보여준다.
    openFiles: () =>
      openView("files", profileRoomId ? { room: profileRoomId } : undefined),
    renamePeerOpen: view === "rename-peer",
    openRenamePeer: () =>
      openView("rename-peer", {
        ...(profileRoomId ? { room: profileRoomId } : {}),
        ...(profilePeerId ? { peer: profilePeerId } : {}),
      }),
    renameSelfOpen: view === "rename-self",
    openRenameSelf: () => openView("rename-self"),
    passwordOpen: view === "password",
    openPassword: () => openView("password"),
    avatarOpen: view === "avatar",
    openAvatar: () => openView("avatar"),
    changeAvatar: (contentBase64: string) => avatarMutation.mutate(contentBase64),
    removeAvatar: () => removeAvatarMutation.mutate(),
    avatarPending: avatarMutation.isPending || removeAvatarMutation.isPending,
    avatarError:
      avatarMutation.error instanceof Error ? avatarMutation.error.message : null,
    addAddressOpen: view === "add-address",
    openAddAddress: () => openView("add-address"),
    /** 지금 쓰는 주소 = 지금 계정. 없으면 쓸 수 있는 주소가 하나도 없는 것이다 */
    identity,
    /** 갈아탈 수 있는 주소들. 기다리는 중이거나 꺼둔 것은 빠져 있다 */
    identityOptions,
    selectIdentity: (identityId: string) => {
      // 갈아타면서 열어둔 방을 들고 가면 남의 계정 방을 보게 된다. 목록으로 나간다.
      selectIdentity(identityId);
      goHome();
    },
    /** 주소별 안 읽음. 갈아타는 자리에 붙는다 */
    unreadByIdentity: countsQuery.data?.byIdentity ?? {},

    /**
     * 손보는 중인 내 주소. 목록에서 찾아 넘긴다 — id만 들고 다니면 창이 이름도
     * 상태도 모르고, 그 사이에 목록이 갱신되면 옛 값이 남는다.
     */
    managedAddress:
      view === "address"
        ? (user.addresses.find((entry) => entry.id === managedAddressId) ?? null)
        : null,
    openAddress: (identityId: string) => openView("address", { address: identityId }),
    setAddressEnabled: (identityId: string, enabled: boolean) => {
      const entry = user.addresses.find((item) => item.id === identityId);
      addressEnableMutation.mutate({
        identityId,
        enabled,
        address: entry?.address ?? "주소",
      });
    },
    removeAddress: (identityId: string) => {
      const entry = user.addresses.find((item) => item.id === identityId);
      addressRemoveMutation.mutate({ identityId, address: entry?.address ?? "주소" });
    },
    addressPending:
      addressEnableMutation.isPending || addressRemoveMutation.isPending,
    addressError:
      addressEnableMutation.error instanceof Error
        ? addressEnableMutation.error.message
        : addressRemoveMutation.error instanceof Error
          ? addressRemoveMutation.error.message
          : null,
    closeView,

    openHtml,
    closeHtml,
    dismissUndo: () => setUndoNotice(null),
    /** 화면 아래에 한 줄 알려준다. 첨부가 너무 클 때 같은 경우. */
    notify: (message: string) => setUndoNotice(message),
    sendPending: sendMutation.isPending,
    sendMessage,
    retryMessage: (messageId: string) => retryMutation.mutate(messageId),

    /** 지금 열린 방에 쓰다 만 글 */
    draft: routeRoomId ? (drafts[routeRoomId] ?? "") : "",
    setDraft: (value: string) => {
      if (routeRoomId === null) return;
      setDrafts((current) => ({ ...current, [routeRoomId]: value }));
    },

    toggleQuiet,
    markAllQuietRead: () => readAllMutation.mutate(),
    startChat,
    startChatPending: roomCreateMutation.isPending,
    signOut,

    // 프로필
    profilePeer,
    renameSelf,
    renamePeer,
    changeReceiveMode,
    toggleBlockPeer,
    changePassword: (input: { current: string; next: string }) =>
      passwordMutation.mutate(input),
    profilePending: profileMutation.isPending || contactMutation.isPending,
    passwordPending: passwordMutation.isPending,
    passwordError:
      passwordMutation.error instanceof Error ? passwordMutation.error.message : null,
    requestAddress: (localPart: string) => addressMutation.mutate(localPart),
    addAddressPending: addressMutation.isPending,
    addAddressError:
      addressMutation.error instanceof Error ? addressMutation.error.message : null,
  } satisfies Record<string, unknown> & { rooms: Room[] };
}
